import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Button, Field, Input, Select, Textarea } from '../../components/ui';
import { api } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  ReferralAudience,
  ReferralCampaign,
  ReferralQualification,
  ReferralRewardType,
} from '../../lib/types';
import {
  describeOffer,
  fromLocalInput,
  parseDeviceIds,
  toLocalInput,
} from './shared';

/** The API's own bounds, so a value it would refuse is caught at the field. */
const MAX_REQUIRED = 1000;
const MAX_DAYLIGHTS = 1_000_000;
const MAX_TRIAL_DAYS = 365;
const MAX_REWARDS = 1000;
const MAX_MEMBERS = 1000;

/**
 * Creates a campaign, or edits one.
 *
 * Built around the one rule an operator can trip over: once somebody has used
 * a code, the terms — what qualifies, how many, what is paid — are what a
 * referrer was promised and cannot change. Those fields are shown locked with
 * the reason, rather than left editable for the API to refuse.
 */
export function CampaignEditor({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: ReferralCampaign | null;
  onClose: () => void;
  onSaved: (campaign: ReferralCampaign) => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const editing = campaign !== null;
  const termsLocked = campaign?.termsLocked ?? false;

  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    description: campaign?.description ?? '',
    audience: campaign?.audience ?? 'all',
    deviceIds: '',
    qualification: campaign?.qualification ?? 'first_top_up',
    requiredReferrals: String(campaign?.requiredReferrals ?? 3),
    rewardType: campaign?.rewardType ?? 'daylights',
    rewardAmount: String(campaign?.rewardAmount ?? 100),
    unlimited: campaign ? campaign.maxRewardsPerReferrer === null : false,
    maxRewardsPerReferrer: String(campaign?.maxRewardsPerReferrer ?? 1),
    startsAt: toLocalInput(campaign?.startsAt ?? null),
    endsAt: toLocalInput(campaign?.endsAt ?? null),
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const members = useMemo(() => parseDeviceIds(form.deviceIds), [form.deviceIds]);

  const required = Number(form.requiredReferrals);
  const amount = Number(form.rewardAmount);
  const maxRewards = form.unlimited ? null : Number(form.maxRewardsPerReferrer);
  const maxAmount = form.rewardType === 'free_trial' ? MAX_TRIAL_DAYS : MAX_DAYLIGHTS;

  /** Every reason the form cannot be sent, first one wins. */
  const problem = useMemo((): string | null => {
    if (form.name.trim().length < 2) return t('referrals.editor.error.name');
    if (!isCount(required, MAX_REQUIRED)) {
      return t('referrals.editor.error.required', { max: MAX_REQUIRED });
    }
    if (!isCount(amount, maxAmount)) {
      return t(
        form.rewardType === 'free_trial'
          ? 'referrals.editor.error.trialDays'
          : 'referrals.editor.error.daylights',
        { max: maxAmount },
      );
    }
    if (maxRewards !== null && !isCount(maxRewards, MAX_REWARDS)) {
      return t('referrals.editor.error.maxRewards', { max: MAX_REWARDS });
    }
    const startsAt = fromLocalInput(form.startsAt);
    const endsAt = fromLocalInput(form.endsAt);
    if (startsAt && endsAt && endsAt < startsAt) {
      return t('referrals.editor.error.window');
    }
    if (!editing && endsAt && new Date(endsAt).getTime() <= Date.now()) {
      return t('referrals.editor.error.endInPast');
    }
    if (!editing && form.audience === 'users') {
      if (members.invalid.length > 0) {
        return t('referrals.editor.error.badIds', {
          ids: members.invalid.slice(0, 3).join(', '),
        });
      }
      if (members.valid.length === 0) return t('referrals.editor.error.noMembers');
      if (members.valid.length > MAX_MEMBERS) {
        return t('referrals.editor.error.tooManyMembers', { max: MAX_MEMBERS });
      }
    }
    return null;
  }, [form, members, required, amount, maxAmount, maxRewards, editing, t]);

  const save = useMutation({
    mutationFn: (reason: string) => {
      const terms = termsLocked
        ? {}
        : {
            qualification: form.qualification,
            requiredReferrals: required,
            rewardType: form.rewardType,
            rewardAmount: amount,
            maxRewardsPerReferrer: maxRewards,
          };
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        ...terms,
        startsAt: fromLocalInput(form.startsAt),
        endsAt: fromLocalInput(form.endsAt),
        reason,
      };

      return campaign
        ? api.patch<ReferralCampaign>(`/api/referrals/campaigns/${campaign.id}`, body)
        : api.post<ReferralCampaign>('/api/referrals/campaigns', {
            ...body,
            audience: form.audience,
            ...(form.audience === 'users' ? { deviceIds: members.valid } : {}),
          });
    },
    onSuccess: (saved) => {
      toast.success(t(editing ? 'referrals.editor.updated' : 'referrals.editor.created'));
      void queryClient.invalidateQueries({ queryKey: ['referrals'] });
      setConfirming(false);
      onSaved(saved);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const preview = isCount(required, MAX_REQUIRED) && isCount(amount, maxAmount)
    ? describeOffer({
        qualification: form.qualification,
        requiredReferrals: required,
        rewardType: form.rewardType,
        rewardAmount: amount,
        maxRewardsPerReferrer:
          maxRewards !== null && isCount(maxRewards, MAX_REWARDS) ? maxRewards : null,
      })
    : null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        campaign
          ? t('referrals.editor.editTitle', { name: campaign.name })
          : t('referrals.editor.newTitle')
      }
      footer={
        <div className="space-y-2">
          {problem && <p className="text-xs text-danger">{problem}</p>}
          <Button
            variant="primary"
            className="w-full"
            disabled={problem !== null}
            onClick={() => setConfirming(true)}
          >
            {editing ? t('common.saveChanges') : t('referrals.editor.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {preview && (
          <div className="flex items-start gap-2 rounded-lg border border-brand/25 bg-brand-soft px-3 py-2.5 text-sm text-brand">
            <Sparkles size={15} className="mt-0.5 shrink-0" />
            <span className="min-w-0 break-words">{preview}</span>
          </div>
        )}

        <section className="space-y-3">
          <Field label={t('common.name')} hint={t('referrals.editor.nameHint')}>
            <Input
              value={form.name}
              maxLength={120}
              onChange={(event) => set('name', event.target.value)}
              placeholder={t('referrals.editor.namePlaceholder')}
            />
          </Field>
          <Field
            label={t('common.description')}
            hint={t('referrals.editor.descriptionHint')}
          >
            <Textarea
              rows={3}
              maxLength={500}
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              placeholder={t('referrals.editor.descriptionPlaceholder')}
            />
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('referrals.editor.section.audience')}
          </h3>
          <Field
            label={t('referrals.field.audience')}
            hint={
              editing
                ? t('referrals.editor.audienceFixed')
                : t(`referrals.audienceHint.${form.audience}`)
            }
          >
            <Select
              value={form.audience}
              disabled={editing}
              onChange={(event) => set('audience', event.target.value as ReferralAudience)}
            >
              <option value="all">{t('referrals.audience.all')}</option>
              <option value="users">{t('referrals.audience.users')}</option>
            </Select>
          </Field>
          {!editing && form.audience === 'users' && (
            <Field
              label={t('referrals.editor.members')}
              hint={t('referrals.editor.membersHint', {
                count: members.valid.length,
                max: MAX_MEMBERS,
              })}
            >
              <Textarea
                rows={4}
                className="font-mono text-xs"
                value={form.deviceIds}
                onChange={(event) => set('deviceIds', event.target.value)}
                placeholder={t('referrals.editor.membersPlaceholder')}
              />
            </Field>
          )}
          {editing && form.audience === 'users' && (
            <p className="text-xs text-ink-3">{t('referrals.editor.membersLater')}</p>
          )}
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('referrals.editor.section.terms')}
            </h3>
            {termsLocked && (
              <span className="inline-flex items-center gap-1 text-xs text-warn">
                <Lock size={12} />
                {t('referrals.editor.termsLocked')}
              </span>
            )}
          </div>
          {termsLocked && (
            <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
              {t('referrals.editor.termsLockedHint')}
            </p>
          )}

          <Field
            label={t('referrals.field.qualification')}
            hint={t(`referrals.qualificationHint.${form.qualification}`)}
          >
            <Select
              value={form.qualification}
              disabled={termsLocked}
              onChange={(event) =>
                set('qualification', event.target.value as ReferralQualification)
              }
            >
              <option value="first_top_up">{t('referrals.qualification.first_top_up')}</option>
              <option value="signup">{t('referrals.qualification.signup')}</option>
            </Select>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('referrals.field.requiredReferrals')}
              hint={t('referrals.editor.requiredHint')}
            >
              <Input
                type="number"
                min={1}
                max={MAX_REQUIRED}
                inputMode="numeric"
                disabled={termsLocked}
                value={form.requiredReferrals}
                onChange={(event) => set('requiredReferrals', event.target.value)}
              />
            </Field>
            <Field label={t('referrals.field.rewardType')}>
              <Select
                value={form.rewardType}
                disabled={termsLocked}
                onChange={(event) =>
                  set('rewardType', event.target.value as ReferralRewardType)
                }
              >
                <option value="daylights">{t('referrals.rewardType.daylights')}</option>
                <option value="free_trial">{t('referrals.rewardType.free_trial')}</option>
              </Select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t(
                form.rewardType === 'free_trial'
                  ? 'referrals.field.trialDays'
                  : 'referrals.field.daylights',
              )}
              hint={
                form.rewardType === 'free_trial'
                  ? t('referrals.editor.trialHint')
                  : t('referrals.editor.daylightsHint')
              }
            >
              <Input
                type="number"
                min={1}
                max={maxAmount}
                inputMode="numeric"
                disabled={termsLocked}
                value={form.rewardAmount}
                onChange={(event) => set('rewardAmount', event.target.value)}
              />
            </Field>
            <Field
              label={t('referrals.field.maxRewards')}
              hint={t('referrals.editor.maxRewardsHint')}
            >
              <div className="space-y-2">
                <Input
                  type="number"
                  min={1}
                  max={MAX_REWARDS}
                  inputMode="numeric"
                  disabled={termsLocked || form.unlimited}
                  value={form.unlimited ? '' : form.maxRewardsPerReferrer}
                  placeholder={form.unlimited ? t('common.noLimit') : undefined}
                  onChange={(event) => set('maxRewardsPerReferrer', event.target.value)}
                />
                <label className="flex items-center gap-2 text-xs text-ink-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand"
                    disabled={termsLocked}
                    checked={form.unlimited}
                    onChange={(event) => set('unlimited', event.target.checked)}
                  />
                  {t('referrals.editor.unlimited')}
                </label>
              </div>
            </Field>
          </div>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('referrals.editor.section.window')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('referrals.field.startsAt')} hint={t('referrals.editor.startsHint')}>
              <Input
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => set('startsAt', event.target.value)}
              />
            </Field>
            <Field label={t('referrals.field.endsAt')} hint={t('referrals.editor.endsHint')}>
              <Input
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => set('endsAt', event.target.value)}
              />
            </Field>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t(editing ? 'referrals.editor.confirmUpdate' : 'referrals.editor.confirmCreate')}
        description={preview ?? undefined}
        confirmLabel={editing ? t('common.save') : t('referrals.editor.create')}
        loading={save.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason) => save.mutate(reason)}
      />
    </Drawer>
  );
}

function isCount(value: number, max: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= max;
}
